/**
 * Iter337 · Hook that mirrors the sidebar drag-order + open-groups
 * state to `/api/user/sidebar-prefs` so it survives login on other
 * devices. Kept as thin as possible so the existing Sidebar internal
 * state model doesn't need to be rewritten.
 *
 * Design
 * ------
 * • On mount, GET the user's saved prefs and merge into the local
 *   setters passed in. Fires a single request per session.
 * • Whenever any slice (topOrder / groupOrders / smithOrder /
 *   moreOrder / openGroups) changes, PUT the delta with a 400 ms
 *   debounce so rapid drags don't hammer the API.
 * • `reset()` returns a function the caller can bind to a "Restore
 *   default order" button in Settings.
 *
 * The hook is a no-op when unauthenticated (no token) so it can safely
 * run inside the marketing shell for demo users.
 */
import { useEffect, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function useSidebarPrefs({
  authToken,
  topOrder,      setTopOrder,
  groupOrders,   setGroupOrders,
  smithOrder,    setSmithOrder,
  moreOrder,     setMoreOrder,
  openGroups,    setOpenGroups,
}) {
  const hydratedRef = useRef(false);
  const inFlightRef = useRef(null);

  // Hydrate once per mount.
  useEffect(() => {
    if (!authToken || hydratedRef.current) return;
    hydratedRef.current = true;
    axios.get(`${API}/user/sidebar-prefs`)
      .then(({ data }) => {
        if (data.top_order?.length && setTopOrder) setTopOrder(data.top_order);
        if (data.group_orders && Object.keys(data.group_orders).length && setGroupOrders) {
          setGroupOrders((prev) => ({ ...(prev || {}), ...data.group_orders }));
        }
        if (data.smith_order?.length && setSmithOrder) setSmithOrder(data.smith_order);
        if (data.more_order?.length && setMoreOrder) setMoreOrder(data.more_order);
        if (data.open_groups && Object.keys(data.open_groups).length && setOpenGroups) {
          setOpenGroups((prev) => ({ ...(prev || {}), ...data.open_groups }));
        }
      })
      .catch(() => { /* first load or offline — keep localStorage state */ });
  }, [authToken, setTopOrder, setGroupOrders, setSmithOrder,
       setMoreOrder, setOpenGroups]);

  // Debounced upsert whenever local state changes.
  useEffect(() => {
    if (!authToken || !hydratedRef.current) return undefined;
    if (inFlightRef.current) clearTimeout(inFlightRef.current);
    inFlightRef.current = setTimeout(() => {
      axios.put(`${API}/user/sidebar-prefs`, {
        top_order: topOrder,
        group_orders: groupOrders,
        smith_order: smithOrder,
        more_order: moreOrder,
        open_groups: openGroups,
      }).catch(() => { /* fail silent — localStorage still holds */ });
    }, 400);
    return () => inFlightRef.current && clearTimeout(inFlightRef.current);
  }, [authToken, topOrder, groupOrders, smithOrder, moreOrder, openGroups]);
}

// Standalone helper used by the Settings page to trigger a hard reset.
export async function resetSidebarPrefs() {
  try {
    await axios.post(`${API}/user/sidebar-prefs/reset`);
    ["alp.sidebar.topOrder", "alp.sidebar.groupOrders",
     "alp.sidebar.smithOrder", "alp.sidebar.moreOrder",
     "alp.sidebar.openGroups"].forEach((k) => {
      try { localStorage.removeItem(k); } catch { /* quota */ }
    });
    return true;
  } catch { return false; }
}
